import { useCallback, useEffect, useRef, Fragment } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import useBreakpoints from '@zenbusiness/zui/useBreakpoints';
import PropTypes from 'prop-types';
import { useSearchParams, useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';

import AddCounterPartyModal from '#client/components/AddCounterPartyModal';
import { FocusGrid, ContainerGrid } from '#client/components/Grid';
import ListAccountSkeleton from '#client/components/ListAccountSkeleton';
import Account from '#client/components/MoveMoney/components/Account';
import RelinkAccount from '#client/components/MoveMoney/components/RelinkAccount';
import UnarchiveAccount from '#client/components/MoveMoney/components/UnarchiveAccount';
import { COUNTERPARTY_CONNECTED_TYPE } from '#client/constants/counterParty';
import { COUNTERPARTY_ID, MODAL_NAMES } from '#client/constants/modals';
import { COUNTER_PARTY_STATUS } from '#client/constants/payees';
import { HOW_MUCH_STEP, NUMBER_OF_ACCOUNTS_TO_SHOW } from '#client/constants/transfer';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCounterparty from '#client/hooks/useCounterparty';
import useFormStep from '#client/hooks/useFormStep';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useSkeleton from '#client/hooks/useSkeleton';
import useToastContext from '#client/hooks/useToastContext';
import { getLast4digits } from '#client/utils/text';

import { DialogContentStyled, Wrapper } from './WhichAccountForm.styles';

const ListAccounts = ({
  accounts,
  resetPagination,
  setShow,
  setHide,
  handleClose,
  handleSelect,
  handlePlaidRedirect
}) => {
  const [searchParams] = useSearchParams();
  const { setData } = useFormStep();

  const hasCounterpartyId = searchParams.get(COUNTERPARTY_ID);

  useEffect(() => {
    if (hasCounterpartyId) {
      setData((prevData) => ({
        ...prevData,
        selectedToAccount: {}
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCounterpartyId]);

  return (
    <Stack gap={{ xs: '8px', md: '12px' }}>
      {accounts.map((account) => {
        const {
          id,
          name,
          accountNumber,
          connectedType,
          bank,
          accountType,
          status,
          isWireSupported,
          isACHSupported
        } = account;
        const last4Digits = getLast4digits(accountNumber) ?? '';
        const handleSelectAccount = () => handleSelect(account);

        if (status === COUNTER_PARTY_STATUS.ACTIVE) {
          return (
            <Account
              accountType={accountType}
              bankName={bank}
              connectedType={connectedType}
              handleSelect={handleSelectAccount}
              isACHSupported={isACHSupported}
              isSendMoney
              isWireSupported={isWireSupported}
              key={id}
              last4Digits={last4Digits}
              name={name}
              numberOfCharacters={1}
            />
          );
        }

        if (connectedType === COUNTERPARTY_CONNECTED_TYPE.PAYEE) {
          return (
            <UnarchiveAccount
              bankName={bank}
              handleCloseModal={handleClose}
              id={id}
              isACHSupported={isACHSupported}
              isSendMoney
              isWireSupported={isWireSupported}
              key={id}
              last4Digits={last4Digits}
              name={name}
              numberOfCharacters={1}
              resetPagination={resetPagination}
              setHideSkeleton={setHide}
              setShowSkeleton={setShow}
            />
          );
        }

        if (connectedType === COUNTERPARTY_CONNECTED_TYPE.PLAID) {
          return (
            <RelinkAccount
              accountType={accountType}
              bankName={bank}
              connectedType={connectedType}
              id={id}
              isACHSupported={isACHSupported}
              isSendMoney
              isWireSupported={isWireSupported}
              key={id}
              last4Digits={last4Digits}
              name={name}
              numberOfCharacters={1}
              onRelink={handlePlaidRedirect}
              openingAt={MODAL_NAMES.SEND_MONEY}
              resetPagination={resetPagination}
              setHideSkeleton={setHide}
              setShowSkeleton={setShow}
              status={status}
            />
          );
        }

        return <Fragment key={id} />;
      })}
    </Stack>
  );
};

ListAccounts.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      accountNumber: PropTypes.string,
      connectedType: PropTypes.string,
      bank: PropTypes.string,
      accountType: PropTypes.string,
      status: PropTypes.string
    })
  ),
  resetPagination: PropTypes.func,
  setShow: PropTypes.func,
  setHide: PropTypes.func,
  handleClose: PropTypes.func,
  handleSelect: PropTypes.func,
  handlePlaidRedirect: PropTypes.func
};

export default function WhichAccountForm({ handleClose, isHandling = false, actionsKit }) {
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { clearAllToast } = useToastContext();
  const {
    sizes: { isMobile }
  } = useBreakpoints();

  const { setCurrentFormStep, setData, data: formData } = useFormStep();
  const {
    data,
    accounts,
    hasMore,
    loadingAccounts,
    isLoadingMore,
    loadMore,
    resetPagination,
    error: counterpartyError
  } = useCounterparty(undefined, COUNTER_PARTY_STATUS.ALL, isHandling);

  const [showSkeleton, setShow, setHide] = useSkeleton(!loadingAccounts && !!data);

  const isShowLoadMoreButton = !showSkeleton && hasMore;

  const { openModal } = useModalContext();

  useModalDisplayTracker({
    elementName: 'send_money_modal',
    elementText: 'send_money_modal',
    appPageName: 'active_banking_dashboard'
  });

  const trackSelectClick = useButtonPressedCallback({
    elementName: 'send_money_to_account',
    elementText: 'Select',
    appPageName: 'send_money_modal'
  });

  const trackAddNewClick = useButtonPressedCallback({
    elementName: 'add_new_payee',
    elementText: 'Add new',
    appPageName: 'send_money_modal'
  });

  const handleSelect = useCallback(
    (selectedToAccount) => {
      clearAllToast();
      trackSelectClick();
      const othersAccounts = accounts
        .filter((account) => account.id !== selectedToAccount.id)
        .slice(0, NUMBER_OF_ACCOUNTS_TO_SHOW - 1);
      setData({
        ...formData,
        selectedToAccount,
        toAccounts: [selectedToAccount, ...othersAccounts],
        // Reset amount and description when selected another account
        amount: 0,
        description: null
      });
      setCurrentFormStep(HOW_MUCH_STEP);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts, clearAllToast, formData, setCurrentFormStep, setData]
  );

  const handleRefetchCounterParty = useCallback(() => {
    setShow();
    resetPagination();
  }, [resetPagination, setShow]);

  const handleOpenAddPayeeModal = useCallback(() => {
    trackAddNewClick();
    openModal({
      name: MODAL_NAMES.LINK_BANK_ACCOUNT_MODAL,
      component: AddCounterPartyModal,
      mode: AddCounterPartyModal.MODAL_TYPES.ADD_BANK_AND_PAYEE,
      refetch: handleRefetchCounterParty,
      actionsKit
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal, handleRefetchCounterParty, navigate, businessEntityUuid]);

  const handlePlaidRedirect = useCallback(() => {
    if (businessEntityUuid) {
      navigate(`/dashboard/${businessEntityUuid}/finances/linked-accounts`);
    }
  }, [navigate, businessEntityUuid]);

  useEffect(() => {
    if (counterpartyError) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterpartyError]);

  useEffect(() => {
    if (formData.isResetCounterparties) {
      resetPagination();
      setShow();
      setData({
        ...formData,
        isResetCounterparties: false
      });
    }
    scrollRef?.current?.scrollIntoView(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  return (
    <DialogContentStyled role="form">
      <ContainerGrid>
        <FocusGrid>
          <Wrapper>
            <Stack>
              <Heading
                align="center"
                family="secondary"
                size="3xl"
                weight="bold"
              >
                Who are you sending money to?
              </Heading>
              <Stack
                direction="row"
                justifyContent="right"
                margin={isMobile ? '32px 0 16px 0' : '40px 0 24px 0'}
              >
                <Button
                  icon={<AddIcon />}
                  onClick={handleOpenAddPayeeModal}
                  size="md"
                  variant="text"
                >
                  Add new
                </Button>
              </Stack>
              <Stack spacing="8px">
                {showSkeleton ? (
                  <ListAccountSkeleton />
                ) : (
                  <ListAccounts
                    accounts={accounts}
                    handleClose={handleClose}
                    handlePlaidRedirect={handlePlaidRedirect}
                    handleSelect={handleSelect}
                    resetPagination={resetPagination}
                    setHide={setHide}
                    setShow={setShow}
                  />
                )}
              </Stack>
              <Box
                marginTop="40px"
                textAlign="center"
              >
                {isShowLoadMoreButton && (
                  <LoadingButton
                    loading={isLoadingMore}
                    onClick={loadMore}
                    variant="outlined"
                  >
                    Load more
                  </LoadingButton>
                )}
              </Box>
            </Stack>
          </Wrapper>
        </FocusGrid>
      </ContainerGrid>
    </DialogContentStyled>
  );
}

WhichAccountForm.propTypes = {
  handleClose: PropTypes.func,
  isHandling: PropTypes.bool,
  actionsKit: PropTypes.shape({
    resetSearchParameters: PropTypes.func
  })
};
